import React, { useCallback, useState } from 'react';
import { useQuery } from '@apollo/client';
import { Link } from 'react-router-dom';
import {
  ColGroup,
  TableBody,
  TableHead,
  CustomPanel,
  CustomDynamicResultTable,
  FormattedDate,
  entitlementUtils,
  useFeatureToggle,
} from '@css-xp/base';
import { fetchCloudSiteOrders } from '../../../queries/fetch-orders';
import {
  DEFAULT_ROWS_PER_PAGE,
  getPurchaseMetadataEntries,
  NO_DETAILS_TO_SHOW,
} from '@atlassian/cssxp-utils-entitlement';
import { generatedTypesNew } from '@css-xp/graphtypes';
import { CONTACT_PATH, CONTACT_PATH_2 } from '@atlassian/cssxp-typescommon';
import './order-history-tab.scss';
import { ENABLE_AAID_FOR_CONTACT_PAGE_REDIRECTION } from '@atlassian/cssxp-utils-generic';

export const OrderHistoryTab = ({ cloudId }: { cloudId: string }) => {
  const [rowsPerPage, setRowsPerPage] = useState(DEFAULT_ROWS_PER_PAGE);
  const [currentPage, setCurrentPage] = useState(1);
  const [isEnabled] = useFeatureToggle();
  const emptyStateProps = {
    header: '',
    description: NO_DETAILS_TO_SHOW,
  };
  const columns = [
    {
      name: 'Subscription',
    },
    {
      name: 'Date',
    },
    {
      name: 'Notes',
      customClassNames: 'col-20',
    },
    {
      name: 'Support ticket',
      customClassNames: 'col-10',
    },
    {
      name: 'Details',
      customClassNames: 'col-20',
    },
    {
      name: 'Placed by',
    },
  ];
  const { data, loading, error } = useQuery<
    generatedTypesNew.FetchCloudSiteOrdersQuery,
    generatedTypesNew.FetchCloudSiteOrdersQueryVariables
  >(fetchCloudSiteOrders, {
    variables: {
      cloudSiteId: cloudId,
      searchOptions: {
        first: rowsPerPage,
        offset: (currentPage - 1) * rowsPerPage,
      },
    },
    displayName: 'fetchOrdersForTransactionAccount',
    notifyOnNetworkStatusChange: true,
    fetchPolicy: 'cache-and-network',
    nextFetchPolicy: 'cache-first',
  });

  const nodes = data?.cloudSiteOrders?.nodes ?? [];
  const totalCount = data?.cloudSiteOrders?.totalCount ?? 0;

  const onPageChange = useCallback((pageNumber: number) => {
    setCurrentPage(pageNumber);
  }, []);

  const handleRowsChange = useCallback(
    (newRowPerPage: number) => {
      if (newRowPerPage === rowsPerPage) {
        return;
      }
      setCurrentPage(1);
      setRowsPerPage(newRowPerPage);
    },
    [rowsPerPage]
  );

  return (
    <CustomPanel
      testId="order-history-txa"
      heading={'Order history'}
      expandedHeader={`Collapse order history view`}
      collapsedHeader={`Expand order history view`}
    >
      <CustomDynamicResultTable
        className="order-history-table-txa"
        testId="order-history-txa"
        isLoading={loading}
        error={error?.toString()}
        noResults={nodes.length < 1}
        emptyStateProps={emptyStateProps}
        totalCount={totalCount}
        currentPage={currentPage}
        rowsPerPage={rowsPerPage}
        onPageChange={onPageChange}
        isPaginationEnabled={nodes.length > 0 && totalCount > DEFAULT_ROWS_PER_PAGE}
        onRowChange={handleRowsChange}
        boxShadow={false}
      >
        <ColGroup columns={columns} />
        <TableHead columns={columns} />
        <TableBody>
          {nodes.map((node, index) => {
            const orderLineItems = node?.orderLineItems?.nodes ?? [];
            return (
              <>
                <tr
                  key={`order-history-txa-${node?.id}`}
                  data-testid={`order-history-txa-parentrow-${index}`}
                  className="grey-row"
                >
                  <td colSpan={6}>
                    <h2>Order number: {node?.orderNumber}</h2>
                  </td>
                </tr>
                {orderLineItems.map((lineItem, childIndex) => {
                  const purchaseMetadata = getPurchaseMetadataEntries(lineItem?.orderLineItemsPurchaseMetadata);
                  return (
                    <tr
                      key={`order-history-txa-${lineItem?.lineItemId}`}
                      data-testid={`order-history-txa-childrow-${childIndex}`}
                      className="white-row"
                    >
                      <td>
                        <entitlementUtils.CloudProductDetailView
                          productId={lineItem?.productId as string}
                          entitlementNumber={lineItem?.entitlementNumber || ''}
                          edition={lineItem?.edition}
                          productName={lineItem?.productName}
                        />
                      </td>
                      <td>
                        <FormattedDate date={node?.created} showTime />
                      </td>
                      <td>
                        <span className="line-clamp">{purchaseMetadata?.note}</span>
                      </td>
                      <td>{node?.metadata?.supportTicketNumber}</td>
                      <td>
                        <entitlementUtils.OrderDetailView orderLineItem={lineItem} />
                      </td>
                      <td>
                        {node?.contextSubject ? (
                          <Link
                            to={`${CONTACT_PATH_2}/${
                              isEnabled(ENABLE_AAID_FOR_CONTACT_PAGE_REDIRECTION) && node?.contextSubjectId
                                ? node.contextSubjectId
                                : window.btoa(node?.contextSubject ?? '')
                            }`}
                          >
                            {node?.contextSubject}
                          </Link>
                        ) : (
                          node?.contextSubject
                        )}
                      </td>
                    </tr>
                  );
                })}
              </>
            );
          })}
        </TableBody>
      </CustomDynamicResultTable>
    </CustomPanel>
  );
};
